import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ROUTES } from '../../../constants/routes';
import {
  addWishlistItem,
  removeWishlistByCourseId,
} from '../../../services/wishlists';

const INSTRUCTOR_AVATAR =
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face';

const STAR_PATH =
  'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

function Star({ fill }) {
  const amount = Math.min(1, Math.max(0, fill));

  if (amount <= 0) {
    return (
      <svg className="h-3 w-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path d={STAR_PATH} />
      </svg>
    );
  }

  if (amount >= 1) {
    return (
      <svg className="h-3 w-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path d={STAR_PATH} />
      </svg>
    );
  }

  return (
    <span className="relative inline-block h-3 w-3 shrink-0">
      <svg className="absolute inset-0 h-3 w-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path d={STAR_PATH} />
      </svg>
      <span className="absolute inset-0 overflow-hidden" style={{ width: `${amount * 100}%` }}>
        <svg className="h-3 w-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
          <path d={STAR_PATH} />
        </svg>
      </span>
    </span>
  );
}

function StarRating({ rating }) {
  const value = Math.min(5, Math.max(0, Number(rating) || 0));
  const display = Number.isInteger(value) ? String(value) : value.toFixed(1);

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm font-semibold text-[#666666]">{display}</span>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} fill={Math.min(1, Math.max(0, value - (star - 1)))} />
        ))}
      </div>
    </div>
  );
}

function FreeCourseCard({ course, className = '', wishlistId, onFavoriteChange, onToggleFavorite }) {
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const [fav, setFav] = useState(Boolean(course.isFavorite));
  const [favLoading, setFavLoading] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const instructorAvatar = course.instructorImage || INSTRUCTOR_AVATAR;
  const showThumbnail = Boolean(course.thumbnailUrl) && !thumbFailed;
  const badgeLabel = course.newLaunchLabel || null;
  const courseDetailUrl = ROUTES.COURSE_DETAIL(course.id);
  const teacherProfileUrl = course.instructorId
    ? ROUTES.TEACHER_DETAIL(course.instructorId)
    : null;

  useEffect(() => {
    setFav(Boolean(course.isFavorite));
  }, [course.id, course.isFavorite]);

  const handleFavoriteClick = async () => {
    if (!isLoggedIn) {
      openAuthPopup();
      return;
    }

    if (favLoading) return;

    const nextFav = !fav;
    setFavLoading(true);

    try {
      if (onToggleFavorite) {
        await onToggleFavorite(nextFav);
      } else if (nextFav) {
        await addWishlistItem(course.id);
      } else {
        await removeWishlistByCourseId(course.id);
      }

      setFav(nextFav);
      onFavoriteChange?.(nextFav);
    } catch {
      setFav(fav);
    } finally {
      setFavLoading(false);
    }
  };

  return (
    <article
      className={`relative flex w-full  sm:w-[300px] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_4px_20px_rgba(0,0,0,0.08)] ${className}`}
    >
      {badgeLabel && (
        <span className="absolute right-0 top-0 z-10 bg-[#1A87CC] px-3 py-1.5 text-[13px] font-medium capitalize tracking-wide text-white">
          {badgeLabel}
        </span>
      )}
      <div className="relative h-[158px] w-full overflow-hidden rounded-t-xl">
        {showThumbnail ? (
          <img
            src={course.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setThumbFailed(true)}
          />
        ) : (
          <div className={`h-full w-full bg-gradient-to-br ${course.gradient}`} />
        )}
        <Link
          to={courseDetailUrl}
          className="absolute inset-0 z-[1]"
          aria-label={`View ${course.title}`}
        />
      </div>

      <div className="flex flex-1 flex-col pt-3">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${
              course.subjectColorCode ? 'text-white' : course.subjectClass
            }`}
            style={course.subjectColorCode ? { backgroundColor: course.subjectColorCode } : undefined}
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" aria-hidden />
            {course.subject}
          </span>
          <span
            className={`shrink-0 rounded-md px-2.5 py-0.5 text-[12px] font-semibold ${course.classBadgeClass}`}
          >
            {course.classLabel}
          </span>
        </div>

        <h3 className="mt-3 line-clamp-2 text-[15px] font-semibold leading-snug text-[#1B1B1B]">
          <Link to={courseDetailUrl} className="hover:text-[#9369CC]">
            {course.title}
          </Link>
        </h3>

        <div className="mt-3 flex items-center gap-2">
          {teacherProfileUrl ? (
            <Link
              to={teacherProfileUrl}
              className="flex min-w-0 flex-1 items-center gap-2 transition hover:opacity-80"
            >
              <img
                src={instructorAvatar}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-stretched"
              />
              <span className="truncate text-sm font-medium text-[#666666] hover:text-[#9369CC]">
                {course.instructorName || 'Instructor'}
              </span>
            </Link>
          ) : (
            <>
              <img
                src={instructorAvatar}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-stretched"
              />
              <span className="text-sm font-medium text-[#666666]">
                {course.instructorName || 'Instructor'}
              </span>
            </>
          )}
          <div className="ml-auto shrink-0 text-[12px] font-semibold text-[#666666]">
            <StarRating rating={course.rating} />
          </div>
        </div>
      </div>

      <div className="mt-3 bg-[#251A33] px-1 pb-3 pt-2.5">
        <div className="flex items-center justify-between gap-1 text-[10px] font-medium text-[#CCCCCC] sm:text-[10px]">
          <span className="flex items-center gap-1 bg-[#534169] rounded-full px-2 py-1">
            <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {course.chapters} Chapters
          </span>
          <span className="flex items-center gap-1 bg-[#534169] rounded-full px-2 py-1">
            <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {course.lectures} Lecture
          </span>
          <span className="flex items-center gap-1 bg-[#534169] rounded-full px-2 py-1">
            <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {course.duration}
          </span>
        </div>

        <div className="mt-2.5 flex items-center gap-2">
          <Link
            to={courseDetailUrl}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-white bg-transparent py-2 text-base font-medium text-white capitalize"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path d="M8 5v14l11-7z" />
            </svg>
            Start Learning
          </Link>
          <button
            type="button"
            onClick={() => void handleFavoriteClick()}
            disabled={favLoading}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-[0.89px] border-white bg-white/90 shadow-sm disabled:opacity-70"
            aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
          >
            <svg
              className={`h-5 w-5 ${fav ? 'fill-[#FF0000] text-[#FF0000]' : 'fill-none text-[#FF0000]'}`}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
}

export default FreeCourseCard;
